import { MinDto } from './other';

/** Declared in display order, which is also the order the backend reports the first blocker in. */
export enum CaseClosingBlocker {
    UNINVOICED_HOURS = 'UNINVOICED_HOURS',
    UNINVOICED_EXPENSES = 'UNINVOICED_EXPENSES',
    UNFINISHED_TASKS = 'UNFINISHED_TASKS',
    UNSETTLED_INVOICES = 'UNSETTLED_INVOICES',
    UNUSED_PREPAYMENTS = 'UNUSED_PREPAYMENTS',
}

export enum ConfidentialityDecision {
    KEEP = 'KEEP',
    SWITCH_OFF = 'SWITCH_OFF',
}

export enum CloseCaseXrChoice {
    DECIDE_LATER = 'DECIDE_LATER',
    NOT_NEEDED = 'NOT_NEEDED',
    REMIND = 'REMIND',
}

/** The codes the close endpoint refuses with when the case changed since its closing check was read. */
const CLOSING_REFUSAL_CODE_PREFIX = 'CLOSED_CASE_';

/** Takes the error of a rejected request: `{ status, data }`, where `data` is the backend's error body. */
export function isClosingRefusal(error: unknown): boolean {
    const code = (error as { data?: { code?: unknown } } | undefined)?.data?.code;
    return typeof code === 'string' && code.startsWith(CLOSING_REFUSAL_CODE_PREFIX);
}

export type ExperienceRecordClosingState = 'WRITTEN' | 'EXCLUDED' | 'NONE';

export interface CaseClosingConditionDto {
    blocker: CaseClosingBlocker;
    isMet: boolean;
}

export interface UninvoicedHoursDto {
    hours: number;
    timecardCount: number;
}

export interface UninvoicedExpensesDto {
    expenseCount: number;
    amounts: { currencyTypeCode: string; amount: number }[];
}

/** An invoice the caller may not read comes with `isAccessible: false` and every other field null. */
export type UnsettledInvoiceDto =
    | {
          isAccessible: true;
          invoiceId: number;
          invoiceNumber: string;
          outstandingAmount: number;
          currencyTypeCode: string;
          paymentState: 'UNPAID' | 'PARTLY_PAID';
      }
    | {
          isAccessible: false;
          invoiceId: null;
          invoiceNumber: null;
          outstandingAmount: null;
          currencyTypeCode: null;
          paymentState: null;
      };

/** A prepayment the caller may not read comes with `isAccessible: false` and every other field null. */
export type UnusedPrepaymentDto =
    | {
          isAccessible: true;
          invoiceId: number;
          invoiceNumber: string;
          remainingAmount: number;
          currencyTypeCode: string;
      }
    | {
          isAccessible: false;
          invoiceId: null;
          invoiceNumber: null;
          remainingAmount: null;
          currencyTypeCode: null;
      };

export interface UnfinishedCaseTaskDto {
    id: number;
    name: string;
    assignedToEmployees: MinDto[];
    deadlineDate: string | null;
}

export interface UnfinishedCaseTasksDto {
    isBlocking: boolean;
    totalCount: number;
    notAccessibleCount: number;
    accessibleTasks: UnfinishedCaseTaskDto[];
}

export interface CaseClosingExperienceRecordDto {
    state: ExperienceRecordClosingState;
    experienceRecordId: number | null;
}

export interface CaseClosingCheckDto {
    matterId: number;
    caseNumber: string;
    name: string;
    fullName: string;
    isConfidential: boolean;
    matterStateTypeCode: string;
    clientParty: MinDto | null;
    supervisingPartner: MinDto | null;
    responsibleLawyer: MinDto | null;
    openedDate: string;
    lastActivityDate: string | null;
    isClosable: boolean;
    /** Only the conditions the firm has switched on, in {@link CaseClosingBlocker} order. */
    conditions: CaseClosingConditionDto[];
    /** Null when the condition is met. */
    uninvoicedHours: UninvoicedHoursDto | null;
    /** Null when the condition is met. */
    uninvoicedExpenses: UninvoicedExpensesDto | null;
    unsettledInvoices: UnsettledInvoiceDto[];
    unusedPrepayments: UnusedPrepaymentDto[];
    unfinishedTasks: UnfinishedCaseTasksDto;
    experienceRecord: CaseClosingExperienceRecordDto;
    isConfidentialReasonRequired: boolean;
}

export interface CloseMatterRequestDto {
    closedDate: string;
    confidentialityDecision: ConfidentialityDecision | null;
    confidentialReason: string | null;
    xrChoice: CloseCaseXrChoice;
}

export interface CaseClosingConfigurationDto {
    requiresNoUnfinishedTasks: boolean;
    requiresNoUnsettledInvoices: boolean;
    requiresNoUnusedPrepayments: boolean;
    isAutomaticClosingEnabled: boolean;
    automaticClosingInactivityDays: number;
    excludedPracticeAreaCount: number;
    isClosingOnInvoiceSendEnabled: boolean;
    isConfidentialReasonRequired: boolean;
}

export type CaseClosingConfigurationUpdateDto = Omit<CaseClosingConfigurationDto, 'excludedPracticeAreaCount'>;

export interface AutomaticClosingPreviewDto {
    caseCount: number;
}

export interface CloseOnSendDto {
    isRequested: boolean;
    requestedBy: MinDto | null;
    requestedAt: string | null;
    confidentialityDecision: ConfidentialityDecision | null;
    confidentialReason: string | null;
    confidentialityDecidedBy: MinDto | null;
    confidentialityDecidedAt: string | null;
    isEditable: boolean;
    isConfidentialReasonRequired: boolean;
    requiresNoUnsettledInvoices: boolean;
}

export interface UpdateCloseOnSendDto {
    isRequested: boolean;
    confidentialityDecision: ConfidentialityDecision | null;
    confidentialReason: string | null;
}
